import { FC, useEffect, useRef, useState } from 'react';
import ValidatedInput, { ValidatedState } from './ValidatedInput';
import FilterDropdown, { FilterOption } from './FilterDropdown';
import { ValidateAddress } from '../../pages/transaction-cart/TransactionCartWithdraw';
import { RequireAlert } from '../../pages/inventory-management/InventoryManagementCollectDeposit';
import { Validators } from '../../types/enums';
import { ADDRESS_CONSTANTS } from '../../constants/common';
import { COUNTRIES_WITH_STATES, COUNTRY_CODES } from '../../constants/data';

interface ValidatedAddressProps {
  validate: boolean;
  setValid: (valid: boolean) => void;
  address: ValidateAddress | undefined;
  setAddress: (address: ValidateAddress) => void;
}

const ValidatedAddress: FC<ValidatedAddressProps> = ({ validate, setValid, address, setAddress }): JSX.Element => {
  const initialFormState = { value: '', valid: false };

  const [address1Input, setAddress1Input] = useState<ValidatedState>(address?.streetAddress ?? initialFormState);
  const [address2Input, setAddress2Input] = useState<ValidatedState>(address?.streetAddress2 ?? initialFormState);
  const [cityInput, setCityInput] = useState<ValidatedState>(address?.city ?? initialFormState);
  const [stateInput, setStateInput] = useState<FilterOption<string>>(address?.state ?? { name: '', value: '' });
  const [zipcodeInput, setZipcodeInput] = useState<ValidatedState>(address?.postalCode ?? initialFormState);
  const [countryInput, setCountryInput] = useState<FilterOption<string>>(address?.country ?? { name: '', value: '' });
  const [phoneInput, setPhoneInput] = useState<ValidatedState>(address?.phone ?? initialFormState);

  const [statesForCountry, setStatesForCountry] = useState<FilterOption<string>[]>([]);
  const prevCountryRef = useRef<string | undefined>(countryInput.value);

  useEffect(() => {
    setValid(
      address1Input.valid &&
        (address2Input.value === '' || address2Input.valid) &&
        cityInput.valid &&
        stateInput.value.length > 0 &&
        zipcodeInput.valid &&
        countryInput.value.length > 0 &&
        phoneInput.valid,
    );
    setAddress({
      streetAddress: address1Input,
      streetAddress2: address2Input,
      city: cityInput,
      state: stateInput,
      postalCode: zipcodeInput,
      country: countryInput,
      phone: phoneInput,
    });
  }, [address1Input, address2Input, cityInput, stateInput, zipcodeInput, countryInput, phoneInput]);

  useEffect(() => {
    if (countryInput.value && COUNTRIES_WITH_STATES[countryInput.value]) {
      setStatesForCountry(COUNTRIES_WITH_STATES[countryInput.value]);
      prevCountryRef.current !== countryInput.value && setStateInput({ name: '', value: '' });
    }
    prevCountryRef.current = countryInput.value;
  }, [countryInput]);

  return (
    <div className="validate-address">
      <div className="form-row">
        <ValidatedInput
          useId="address-1"
          label={`*${ADDRESS_CONSTANTS.ADDRESS_LINE_1}:`}
          type="text"
          validate={validate}
          setValidatedState={setAddress1Input}
          validators={[Validators.REQUIRED, Validators.SHIPPING_ADDRESS_LENGTH]}
          validatedStateForAutoFill={address1Input}
        />
      </div>
      <div className="form-row">
        <ValidatedInput
          useId="address-2"
          label={`${ADDRESS_CONSTANTS.ADDRESS_LINE_2}:`}
          type="text"
          validate={validate}
          setValidatedState={setAddress2Input}
          validators={[Validators.SHIPPING_ADDRESS_LENGTH]}
          validatedStateForAutoFill={address2Input}
        />
      </div>
      <div className="form-row">
        <label>
          <div>
            *{ADDRESS_CONSTANTS.COUNTRY}:
            <RequireAlert isActive={validate && !countryInput.value} />
          </div>
        </label>
        <div className="input-container">
          <FilterDropdown options={COUNTRY_CODES} value={countryInput} onChange={setCountryInput} />
        </div>
      </div>
      <div className="form-row">
        <label>
          <div>
            *{ADDRESS_CONSTANTS.STATE}:
            <RequireAlert isActive={validate && !stateInput.value} />
          </div>
        </label>
        <div className="input-container">
          <FilterDropdown options={statesForCountry} value={stateInput} onChange={setStateInput} />
        </div>
      </div>
      <div className="form-row">
        <ValidatedInput
          useId="city"
          label={`*${ADDRESS_CONSTANTS.CITY}:`}
          type="text"
          validate={validate}
          setValidatedState={setCityInput}
          validators={[Validators.REQUIRED]}
          validatedStateForAutoFill={cityInput}
        />
      </div>
      <div className="form-row">
        <ValidatedInput
          useId="zipcode"
          label={`*${ADDRESS_CONSTANTS.ZIP_CODE}:`}
          type="text"
          validate={validate}
          setValidatedState={setZipcodeInput}
          validators={[Validators.REQUIRED, Validators.ZIP_CODE]}
          validatedStateForAutoFill={zipcodeInput}
        />
      </div>
      <div className="form-row">
        <ValidatedInput
          useId="contact-phone"
          label={`*${ADDRESS_CONSTANTS.CONTACT_PHONE}:`}
          type="phone"
          validate={validate}
          setValidatedState={setPhoneInput}
          validators={[Validators.REQUIRED, Validators.SHIPPING_PHONE_LENGTH]}
          validatedStateForAutoFill={phoneInput}
        />
      </div>
    </div>
  );
};

export default ValidatedAddress;
